import { View } from '@play-co/timestep-core/ui';
import { Feature } from 'src/replicant/analytics';
import { PartialRecord } from 'src/replicant/utils/types';
import PopupMatch2BaseFullscreen from 'src/game/minigame/match2-timestep/components/PopupMatch2BaseFullscreen';
import LeaderboardsHeader from '../components/LeaderboardsHeader';
import LeaderboardsNav, {
  LeaderboardsNavConfig,
} from '../components/LeaderboardsNav';
import BaseLeaderboardsPage from '../components/BaseLeaderboardsPage';
import LeaderboardsWeeklyPage from '../components/LeaderboardsWeeklyPage';
import LeaderboardsFriendsPage from '../components/LeaderboardsFriendsPage';
import LeaderboardsPlayersPage from '../components/LeaderboardsPlayersPage';
import { AssetGroup } from '@play-co/timestep-core';
import type { WeeklyLeaderboardOriginFeature } from 'src/replicant/weeklyLeaderboard/weeklyLeaderboard.analytics.schema';

type LeaderboardsPageId = 'weekly' | 'friends' | 'players';

export default class PopupWeeklyLeaderboard extends PopupMatch2BaseFullscreen {
  private readonly header: LeaderboardsHeader;
  protected readonly nav: LeaderboardsNav;
  private pageView!: View;
  private pages!: Record<LeaderboardsPageId, () => BaseLeaderboardsPage>;
  private cachedPages: PartialRecord<LeaderboardsPageId, BaseLeaderboardsPage> =
    {};
  private currentPageId!: LeaderboardsPageId;
  private originFeature!: WeeklyLeaderboardOriginFeature;

  constructor(opts: { superview: View; close: () => void }) {
    const headerHeight = 215;

    super({
      superview: opts.superview,
      zIndex: 10000,
      close: () => opts.close(),
      tracking: {
        feature: Feature.WEEKLY_LEADERBOARD._,
        $subFeature: Feature.WEEKLY_LEADERBOARD.POPUP_LEADERBOARD,
      },
      bgImageOpts: {
        backgroundColor: '#204CA2',
      },
    });

    const superview = this.panel;
    const { width } = superview.style;

    this.pageView = new View({
      superview: this.panel,
      width: this.panel.style.width,
      height: this.panel.style.height - headerHeight,
      y: headerHeight,
    });

    this.pages = this.createPages(this.pageView);

    this.header = new LeaderboardsHeader({
      superview,
      width: width,
      height: headerHeight,
    });

    this.nav = new LeaderboardsNav({
      superview,
      width,
      height: 100,
      y: 125,
    });

    this.buttonClose?.updateOpts({ y: 63 });
  }

  public init({
    selectedTab,
    originFeature,
  }: {
    selectedTab?: number;
    originFeature: WeeklyLeaderboardOriginFeature;
  }) {
    super.init({});
    this.originFeature = originFeature;
    const config = this.getNavConfig();
    config.selectedIndex = selectedTab ?? config.selectedIndex;
    this.nav.setup(config);
  }

  protected getNavConfig(): LeaderboardsNavConfig {
    const tabs: LeaderboardsNavConfig['tabs'] = [
      {
        label: 'weeklyLeaderboard.tab_weekly',
        onClick: () => this.goToPage('weekly'),
        tracking: {
          feature: Feature.WEEKLY_LEADERBOARD._,
          $subFeature: Feature.WEEKLY_LEADERBOARD.TAB_WEEKLY,
        },
      },
      {
        label: 'weeklyLeaderboard.tab_friends',
        onClick: () => this.goToPage('friends'),
        tracking: {
          feature: Feature.WEEKLY_LEADERBOARD._,
          $subFeature: Feature.WEEKLY_LEADERBOARD.TAB_FRIENDS,
        },
      },
      {
        label: 'weeklyLeaderboard.tab_players',
        onClick: () => this.goToPage('players'),
        tracking: {
          feature: Feature.WEEKLY_LEADERBOARD._,
          $subFeature: Feature.WEEKLY_LEADERBOARD.TAB_PLAYERS,
        },
      },
    ];
    return {
      tabs,
      selectedIndex: 0,
    };
  }

  protected createPage(page: BaseLeaderboardsPage) {
    page.on('Close', () => this.opts.close?.());
    page.hide();
    return page;
  }

  protected createPages(
    pageView: View,
  ): Record<LeaderboardsPageId, () => BaseLeaderboardsPage> {
    const pages = {
      weekly: () => this.createPage(new LeaderboardsWeeklyPage(pageView)),
      friends: () => this.createPage(new LeaderboardsFriendsPage(pageView)),
      players: () => this.createPage(new LeaderboardsPlayersPage(pageView)),
    };

    return pages;
  }

  protected goToPage(id: LeaderboardsPageId) {
    if (this.currentPageId) {
      this.cachedPages[this.currentPageId]?.hide();
    }

    this.currentPageId = id;

    // Create page if it doesn't exist yet.
    if (!this.cachedPages[this.currentPageId]) {
      this.cachedPages[this.currentPageId] = this.pages[this.currentPageId]();
    }
    this.cachedPages[this.currentPageId]!.originFeature = this.originFeature;
    this.cachedPages[this.currentPageId]!.show();
  }

  protected getAssetsGroup() {
    return new AssetGroup(
      AssetGroup.constructURLs(['assets/ui/weeklyLeaderboard/']),
    );
  }
}
